import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { AlertTriangle } from 'lucide-react';
import { SERVICE_LABELS } from '../../utils/calculatorStaleness';

/**
 * Guard modal shown when the user clicks Save Quote while one or more
 * services have inputs that diverge from their last calculated snapshot.
 *
 * Three buttons (top → bottom):
 *   1. Recalculate & Save  (primary, default focus)
 *   2. Save Anyway         (warning-styled outline)
 *   3. Cancel              (ghost)
 */
const SaveStaleGuardModal = ({
  open,
  staleServices = [],
  onRecalcAndSave,
  onSaveAnyway,
  onCancel,
  isWorking = false,
}) => {
  const labels = staleServices.map((s) => SERVICE_LABELS[s] || s);

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <AlertDialogContent data-testid="calc-save-guard-modal">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Unsaved changes detected
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <div>
                {labels.length === 1
                  ? '1 service has changes that haven\'t been recalculated:'
                  : `${labels.length} services have changes that haven\u2019t been recalculated:`}
              </div>
              <ul className="list-disc list-inside text-sm font-medium">
                {labels.map((label, i) => (
                  <li key={i} data-testid="calc-save-guard-stale-item">{label}</li>
                ))}
              </ul>
              <div className="pt-1 text-sm">
                Saving now will store the previous pricing. Recalculate to
                pick up your latest inputs.
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button
            data-testid="calc-save-guard-recalc-and-save"
            autoFocus
            disabled={isWorking}
            onClick={onRecalcAndSave}
            className="w-full"
          >
            {isWorking ? 'Recalculating…' : 'Recalculate & Save'}
          </Button>
          <Button
            data-testid="calc-save-guard-save-anyway"
            variant="outline"
            disabled={isWorking}
            onClick={onSaveAnyway}
            className="w-full border-amber-400 text-amber-700 hover:bg-amber-50"
          >
            Save Anyway
          </Button>
          <Button
            data-testid="calc-save-guard-cancel"
            variant="ghost"
            disabled={isWorking}
            onClick={onCancel}
            className="w-full"
          >
            Cancel
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SaveStaleGuardModal;
